import React, { useRef, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import useCartTotalsUtils from '../../../hooks/useCartTotalsUtils';
import { useProduct } from '../../../hooks/useProduct';
import { CHECKOUT_CC_URL } from '../../../helpers/urlConst';
import UiContext from '../../../store/uiContext';
import DataContext from '../../../store/dataContext';
import useApiStatus from '../../../store/apiStatus/useApiStatus';
import BillingCheckbox from '../../../components/BillingCheckbox';
import BillingPhone from '../../../components/BillingPhone';
import useAuthentication from '../../authentication/hooks/use-authentication';
import { usePickup } from '../../shipping/containers/pickup-in-store/use-pickup';

import { selectShipping } from '../../shipping/slice';

import { messageType } from '../constants';
import useEventsEffect from '../hooks/use-events';

import style from './index.module.scss';

// eslint-disable-next-line global-require
window.Buffer = window.Buffer || require('buffer').Buffer;

// eslint-disable-next-line import/no-extraneous-dependencies
const XXH = require('xxhashjs');

const CreditCardApp = ({ flags }) => {
  const iframeRef = useRef(null);
  const { getOrderTotal } = useCartTotalsUtils();

  const { checkoutStage, TO_USER_ADD, allowBillingAddressAdd, setIsShippingSectionCollapsed } = useContext(UiContext);
  const {
    portalSettings,
    apiShippingAddressResponse,
    apiEmailResponse,
    cartId,
    submitFailedTimestamp,
    apiPostOrderError,
    contextShippingAddress,
    email,
    setUserCardId,
    setUserCardCvv,
    setCardType,
    setCvvChanged,
    setLast4Numbers,
    contextBillingAddress,
    areSuggestionsVisible,
    setAreSuggestionsVisible,
    cartTotals,
    merchantId,
    platformCartId,
    storeUrl,
  } = useContext(DataContext);

  const total = getOrderTotal();

  const { t } = useTranslation();

  const { set: setApiStatus } = useApiStatus();
  const { allDigital } = useProduct();

  const { isPickupActive } = usePickup();
  const checkPickup = isPickupActive();

  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [isIframeInvisible, setIsIframeInvisible] = useState(false);

  const shipping = useSelector(selectShipping);

  const { isLoggedIn } = useAuthentication();

  const isFieldsetDisabled = checkoutStage === TO_USER_ADD;

  const showBillingCheckbox = () =>
    portalSettings?.checkoutMode?.mode &&
    portalSettings?.checkoutMode?.mode !== 'ACTIVE' &&
    shipping.fullName?.value &&
    contextShippingAddress?.line1 &&
    email;

  const showBillingPhone = () =>
    ((apiPostOrderError?.errorCode?.id === 'NF_CHECKOUT_0008' || portalSettings.contactPhone.isVisible) &&
      !allowBillingAddressAdd) ||
    (portalSettings?.newsletter?.consent?.hasSms && !allowBillingAddressAdd);

  const getAddressHash = address => {
    const toHash = [address.line1, address.line2, address.city, address.region, address.postal, address.countryIso2]
      .join('|')
      .toLowerCase();

    return XXH.h64(Buffer.from(toHash), 0).toString(36);
  };

  const handleCvvOmit = omitCvv =>
    omitCvv.shouldOmit &&
    omitCvv.addressHash ===
      getAddressHash(contextShippingAddress?.line1 ? contextShippingAddress : contextBillingAddress);

  const sendMessageToCcIframe = (type, iframeInitialLoad = false) => {
    const dataToAutofill = allDigital() ? apiEmailResponse?.suggestions : apiShippingAddressResponse;
    if (isIframeLoaded || iframeInitialLoad) {
      switch (type) {
        case messageType.CC_INITIAL_STATE: {
          iframeRef.current.contentWindow.postMessage(
            {
              type: messageType.CC_INITIAL_STATE,
              payload: {
                isFieldsetDisabled,
                theme: portalSettings.theme,
                storeLanguage: portalSettings.storeLanguage,
                requiredCvv: portalSettings.requiredCvv,
                nfCartId: cartId || '',
                userId: email,
                merchantId,
                platformCartId,
                storeUrl,
              },
            },
            CHECKOUT_CC_URL,
          );
          break;
        }
        case messageType.CC_UPDATE_STATE: {
          iframeRef.current.contentWindow.postMessage(
            {
              type: messageType.CC_UPDATE_STATE,
              payload: {
                isFieldsetDisabled,
                theme: portalSettings.theme,
                nfCartId: cartId || '',
                isLoggedIn,
                userId: email,
              },
            },
            CHECKOUT_CC_URL,
          );
          break;
        }
        case messageType.CC_AUTOFILL:
          iframeRef.current.contentWindow.postMessage(
            {
              type: messageType.CC_AUTOFILL,
              payload: {
                apiShippingAddressResponse: {
                  ...dataToAutofill,
                  cards: checkPickup
                    ? []
                    : dataToAutofill?.cards
                        ?.filter(item => item.id !== '0000')
                        ?.map(item => ({
                          ...item,
                          omitCvv: handleCvvOmit(item.omitCvv),
                        })),
                },
                isFieldsetDisabled,
              },
            },
            CHECKOUT_CC_URL,
          );
          break;
        case messageType.CC_EMPTY_ADDRESS:
          iframeRef.current.contentWindow.postMessage(
            {
              type: messageType.CC_EMPTY_ADDRESS,
            },
            CHECKOUT_CC_URL,
          );
          break;
        case messageType.CC_SUBMIT_FAILED:
          iframeRef.current.contentWindow.postMessage(
            {
              type: messageType.CC_SUBMIT_FAILED,
              payload: {
                submitFailedTimestamp,
                apiPostOrderError,
              },
            },
            CHECKOUT_CC_URL,
          );
          break;
        default:
          // eslint-disable-next-line no-console
          console.error(`Couldn't post message to the Credit Card App`);
          break;
      }
    }
  };

  const handleOnLoad = () => {
    setIsIframeLoaded(true);
  };

  const registerMessages = e => {
    const matchUrl = e.origin.split('/').splice(0, 3).join('/');
    if (e.origin === matchUrl) {
      if (e.data.type === messageType.CC_IS_READY) {
        setApiStatus({ isCardReady: e.data.payload.isReady });
        setUserCardId(e.data.payload.userCardId);
        setUserCardCvv(e.data.payload.userCardCvv);
        setCardType(e.data.payload.cardType);
        setCvvChanged(e.data.payload.cvvChanged);
        setLast4Numbers(e.data.payload.last4Numbers);
      } else if (e.data.type === messageType.CC_SUGGESTIONS_ARE_VISIBLE) {
        setAreSuggestionsVisible(e.data.payload.shouldRenderSuggestions);
      }
    }
  };

  const isError0008 = () => apiPostOrderError?.errorCode?.id === 'NF_CHECKOUT_0008';

  const isError0009 = () => apiPostOrderError?.errorCode?.id === 'NF_CHECKOUT_0009';

  const isError0110 = () => apiPostOrderError?.errorCode?.id === 'NF_CHECKOUT_0110';

  const renderHeaderAlerts = () => {
    if (isError0008()) {
      return <div className="declined-error-message">{t('card_add.card_declined_please_verify')}</div>;
    }
    if (isError0009()) {
      return <p className="billing-information-warning">{t('card_add.looks_like_we_need')}</p>;
    }
    if (isError0110() && contextBillingAddress && Object.keys(contextBillingAddress).length > 0) {
      return <div className="declined-error-message">{t('card_add.card_declined_please_verify')}</div>;
    }
    if (isError0110()) {
      return <div className="declined-error-message">{t('card_add.card_declined_please_provide')}</div>;
    }
    return null;
  };

  useEffect(() => {
    if (isIframeLoaded && portalSettings.storeLanguage) {
      sendMessageToCcIframe(messageType.CC_INITIAL_STATE, true);
    }
  }, [isIframeLoaded, portalSettings]);

  useEffect(() => {
    window.addEventListener('message', registerMessages);
    return () => {
      window.removeEventListener('message', registerMessages);
    };
  }, [registerMessages]);

  useEffect(() => {
    sendMessageToCcIframe(messageType.CC_UPDATE_STATE);
  }, [isFieldsetDisabled]);

  useEventsEffect(iframeRef);

  useEffect(() => {
    if (allDigital() && apiEmailResponse.suggestions) {
      sendMessageToCcIframe(messageType.CC_AUTOFILL);
    } else if (apiShippingAddressResponse) {
      sendMessageToCcIframe(messageType.CC_AUTOFILL);
    }
  }, [apiShippingAddressResponse, apiEmailResponse]);

  useEffect(() => {
    if (!shipping.address) {
      sendMessageToCcIframe(messageType.CC_EMPTY_ADDRESS);
    }
  }, [shipping.address]);

  useEffect(() => {
    if (submitFailedTimestamp) {
      if (isError0009() || isError0110()) {
        setIsShippingSectionCollapsed(true);
      }
      sendMessageToCcIframe(messageType.CC_SUBMIT_FAILED, apiPostOrderError);
    }
  }, [submitFailedTimestamp]);

  useEffect(() => {
    sendMessageToCcIframe(messageType.CC_AUTOFILL, false);
  }, [checkPickup]);

  useEffect(() => {
    if (cartTotals?.data?.grandTotal === 0) {
      setApiStatus({ isCardReady: true });
      setIsIframeInvisible(true);
    } else {
      setIsIframeInvisible(false);
    }
  }, [cartTotals?.data?.grandTotal]);

  useEffect(() => {
    if (total === 0) {
      setApiStatus({ isCardReady: true });
      setIsIframeInvisible(true);
    } else {
      setIsIframeInvisible(false);
    }
  }, [total]);

  return (
    <>
      {renderHeaderAlerts()}
      {areSuggestionsVisible ? <div className={style.placeholder} /> : null}
      <iframe
        src={CHECKOUT_CC_URL}
        title={t('payment_method.credit_card.label')}
        ref={iframeRef}
        onLoad={handleOnLoad}
        className={classNames(style.wrapper, {
          [style.invisible]: isIframeInvisible,
          [style.suggestions]: areSuggestionsVisible,
          [style.nosuggestions]: !areSuggestionsVisible,
        })}
        scrolling="no"
      ></iframe>
      {showBillingCheckbox() && <BillingCheckbox />}
      {showBillingPhone() && <BillingPhone flags={flags} />}
    </>
  );
};

CreditCardApp.propTypes = {
  flags: PropTypes.shape({
    'disable-ddi-phone': PropTypes.bool,
  }),
};

export default CreditCardApp;
